import { DexServiceConfig, LogData, LogLevel, SwapParams } from "../types/dex";

interface WorkerInstance {
  worker: Worker;
  busy: boolean;
  id: number;
  resolveInit?: () => void; // 添加解析初始化的函数
  active: boolean; // 标记worker是否已激活并可以接收消息
}

type WorkerMessageHandler = (event: MessageEvent) => void;

interface WorkerMessageData {
  type:
    | "INIT"
    | "SWAP"
    | "BATCH_SWAP"
    | "GET_QUOTE"
    | "TERMINATE"
    | "SWAP_SUCCESS"
    | "BATCH_SWAP_SUCCESS"
    | "QUOTE_SUCCESS"
    | "ERROR"
    | "LOG"
    | "INIT_SUCCESS"
    | "UPDATE_CONFIG";
  data?: any;
  error?: string;
}

interface WorkerError extends Error {
  code?: string;
  details?: unknown;
}

// 定义worker响应结果接口
interface WorkerResponse {
  workerId: number;
  success: boolean;
  data?: any;
  error?: string;
}

export class WorkerPoolService {
  private workers: WorkerInstance[] = [];
  private config: Omit<DexServiceConfig, "onLog">;
  private onLog: (log: LogData) => void;
  private maxWorkers: number;
  private nextWorkerId = 0;
  
  constructor(
    config: Omit<DexServiceConfig, "onLog">,
    maxWorkers: number,
    onLog: (log: LogData) => void
  ) {
    this.config = config;
    this.maxWorkers = maxWorkers;
    this.onLog = onLog;
    this.initializeWorkerPool();
  }

  private async initializeWorkerPool() {
    // 创建初始的worker池
    const workerPromises = [];
    for (let i = 0; i < this.maxWorkers; i++) {
      workerPromises.push(this.createWorker());
    }
    await Promise.all(workerPromises);
    this.onLog({
      level: LogLevel.INFO,
      message: `初始化了 ${this.workers.length} 个worker实例`,
      timestamp: Date.now(),
    });
    return [...this.workers];
  }

  private createWorker(): Promise<WorkerInstance> {
    return new Promise((resolve) => {
      const worker = new Worker(
        new URL("../workers/dex.worker.ts", import.meta.url),
        { type: "module" }
      );

      const workerId = this.nextWorkerId++;
      const instance: WorkerInstance = {
        worker,
        busy: false,
        id: workerId,
        resolveInit: () => {
          instance.active = true; // 初始化成功后标记为激活状态
          this.onLog({
            level: LogLevel.INFO,
            message: `Worker ${workerId} 初始化成功并激活`,
            timestamp: Date.now(),
          });
          resolve(instance);
        },
        active: false // 初始状态为未激活
      };

      // 设置消息处理器
      worker.addEventListener("message", this.createMessageHandler(instance));

      // 初始化worker
      worker.postMessage({
        type: "INIT",
        data: this.config,
      });
    
      this.workers.push(instance);
    });
  }

  private createMessageHandler(instance: WorkerInstance): WorkerMessageHandler {
    return (event: MessageEvent<WorkerMessageData>) => {
      const { type, data, error } = event.data;
      
      this.onLog({
        level: LogLevel.INFO,
        message: `Worker ${instance.id} 收到消息: ${type}`,
        timestamp: Date.now(),
      });

      switch (type) {
        case "LOG":
          this.onLog(data as LogData);
          break;

        case "ERROR":
          this.onLog({
            level: LogLevel.ERROR,
            message: `Worker ${instance.id} error: ${error}`,
            timestamp: Date.now(),
          });
          instance.busy = false;
          break;

        case "SWAP_SUCCESS":
        case "BATCH_SWAP_SUCCESS":
        case "QUOTE_SUCCESS":
          instance.busy = false;
          break;
          
        case "INIT_SUCCESS":
          // 使用实例特定的resolve函数
          if (instance.resolveInit) {
            instance.resolveInit();
            delete instance.resolveInit; // 使用后删除，避免内存泄漏
          }
          break;
      }
    };
  }

  // 向所有激活的worker广播消息
  private broadcastToAllWorkers(type: string, data: any): void {
    const activeWorkers = this.workers.filter(w => w.active);
    
    if (activeWorkers.length === 0) {
      this.onLog({
        level: LogLevel.WARNING,
        message: `没有激活的worker可用，无法广播消息: ${type}`,
        timestamp: Date.now(),
      });
      return;
    }
    
    this.onLog({
      level: LogLevel.INFO,
      message: `向 ${activeWorkers.length} 个worker广播消息: ${type}`,
      timestamp: Date.now(),
    });
    
    // 向所有激活的worker发送消息
    activeWorkers.forEach(instance => {
      instance.worker.postMessage({
        type,
        data,
      });
    });
  }

  // 等待所有worker的响应
  private async waitForAllWorkersResponse(taskType: string): Promise<WorkerResponse[]> {
    const activeWorkers = this.workers.filter(w => w.active);
    
    if (activeWorkers.length === 0) {
      throw new Error("没有激活的worker可用");
    }
    
    const successType =
      taskType === "SWAP"
        ? "SWAP_SUCCESS"
        : taskType === "BATCH_SWAP"
        ? "BATCH_SWAP_SUCCESS"
        : "QUOTE_SUCCESS";
    
    return new Promise((resolve, reject) => {
      // 跟踪每个worker的响应
      const responses: WorkerResponse[] = [];
      const pendingWorkers = new Set(activeWorkers.map(w => w.id));
      
      // 为每个worker添加消息处理器
      const handlers: { worker: Worker; handler: (event: MessageEvent) => void; workerId: number }[] = [];
      
      const checkAllResponded = () => {
        if (pendingWorkers.size === 0) {
          // 所有worker都已响应，移除所有处理器
          handlers.forEach(h => h.worker.removeEventListener("message", h.handler));
          resolve(responses);
        }
      };
      
      activeWorkers.forEach(({ worker, id }) => {
        const handler = (event: MessageEvent<WorkerMessageData>) => {
          const { type, data, error } = event.data;
          
          if (type === successType || type === "ERROR") {
            // 记录响应
            responses.push({
              workerId: id,
              success: type === successType,
              data: type === successType ? data : undefined,
              error: type === "ERROR" ? error : undefined
            });
            
            // 从待处理列表中移除
            pendingWorkers.delete(id);
            
            // 检查是否所有worker都已响应
            checkAllResponded();
          }
        };
        
        worker.addEventListener("message", handler);
        handlers.push({ worker, handler, workerId: id });
      });
      
      // 注意：已移除超时限制，允许worker无限制时间响应
    });
  }

  public async swap(walletIndex: number, params: SwapParams): Promise<WorkerResponse[]> {
    this.broadcastToAllWorkers("SWAP", { walletIndex, params });
    return this.waitForAllWorkersResponse("SWAP");
  }

  public async batchSwap(params: SwapParams[]): Promise<WorkerResponse[]> {
    this.broadcastToAllWorkers("BATCH_SWAP", params);
    return this.waitForAllWorkersResponse("BATCH_SWAP");
  }

  public async getQuote(params: SwapParams): Promise<WorkerResponse[]> {
    this.broadcastToAllWorkers("GET_QUOTE", { params });
    return this.waitForAllWorkersResponse("GET_QUOTE");
  }

  public terminate() {
    // 终止所有worker
    this.workers.forEach(({ worker }) => {
      worker.postMessage({ type: "TERMINATE" });
      worker.terminate();
    });
    this.workers = [];
  }

  public async setMaxWorkers(count: number) {
    // 调整worker数量
    const oldCount = this.workers.length;
    this.maxWorkers = 1;
    
    // this.onLog({
    //   level: LogLevel.INFO,
    //   message: `调整worker数量: ${oldCount} -> ${count}`,
    //   timestamp: Date.now(),
    // });

    // 向所有现有worker发送更新maxThreads的消息
    this.workers.forEach(({ worker }) => {
      worker.postMessage({ 
        type: "UPDATE_CONFIG", 
        data: { maxThreads: count } 
      });
    });

    // 如果需要创建更多worker
    // if (count > oldCount) {
    //   const workerPromises = [];
    //   for (let i = oldCount; i < count; i++) {
    //     workerPromises.push(this.createWorker());
    //   }
      
    //   this.onLog({
    //     level: LogLevel.INFO,
    //     message: `正在创建 ${count - oldCount} 个新worker实例...`,
    //     timestamp: Date.now(),
    //   });
      
    //   await Promise.all(workerPromises);
      
    //   this.onLog({
    //     level: LogLevel.INFO,
    //     message: `成功创建 ${count - oldCount} 个新worker实例，当前共有 ${this.workers.length} 个worker`,
    //     timestamp: Date.now(),
    //   });
    // } else if (count < oldCount) {
    //   // 需要减少worker
    //   const workersToRemove = this.workers.slice(count);
    //   this.workers = this.workers.slice(0, count);

    //   // 终止多余的worker
    //   workersToRemove.forEach(({ worker, id }) => {
    //     worker.postMessage({ type: "TERMINATE" });
    //     worker.terminate();
    //     this.onLog({
    //       level: LogLevel.INFO,
    //       message: `终止并移除worker ${id}`,
    //       timestamp: Date.now(),
    //     });
    //   });
    // }
  }

  public getWorkerCount(): number {
    return this.workers.length;
  }

  public getBusyWorkerCount(): number {
    return this.workers.filter((w) => w.busy).length;
  }
  
  // 获取激活的worker数量
  public getActiveWorkerCount(): number {
    return this.workers.filter((w) => w.active).length;
  }
  
  // 获取worker状态信息，用于调试
  public getWorkerStatus(): Array<{id: number, busy: boolean, active: boolean}> {
    return this.workers.map(w => ({
      id: w.id,
      busy: w.busy,
      active: w.active
    }));
  }
}
